import axios from "axios";
import { API_URL } from "@/config";
import { getOptionsCacheKey } from "@/utils/cache";
import { resetStateToDefault } from "@/lib/store/index";

const defaultState = Object.freeze({
  options: [],
});

const store = {
  namespaced: true,

  state: {
    ...defaultState,
  },

  mutations: {
    setOptions(state, { options, criteria }) {
      state.options = options;
      state.lastOptionsCriteria = getOptionsCacheKey(criteria);
    },

    clearOptionsCache(state) {
      state.options = [];
    },

    resetState(state) {
      resetStateToDefault(state, defaultState);
    },
  },

  actions: {
    async getMany(_, criteria) {
      const { page, ...filters } = criteria;

      const res = await axios.get(`${API_URL}/childrenGroups`, {
        params: { page, ...filters?.filters },
      });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOne(_, institutionWorkersGroupId) {
      const res = await axios.get(`${API_URL}/childrenGroups/${institutionWorkersGroupId}`);

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async update({ commit }, { id, data }) {
      const res = await axios.put(`${API_URL}/childrenGroups/${id}`, data);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return res.data;
    },

    async create({ commit }, data) {
      const res = await axios.post(`${API_URL}/childrenGroups`, data);

      if (res.status !== 201 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return res.data;
    },

    async remove({ commit }, id) {
      const res = await axios.delete(`${API_URL}/childrenGroups/${id}`);

      if (res.status !== 204) {
        throw new Error("Error occurred while processing your request.");
      }

      commit("clearOptionsCache");

      return true;
    },

    async getOptions({ commit, state }, criteria = {}) {
      const cacheKey = getOptionsCacheKey(criteria);

      if (state.options?.length && state.lastOptionsCriteria === cacheKey) {
        return state.options;
      }

      const response = await axios.get(`${API_URL}/childrenGroups/options`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      commit("setOptions", { options: response.data, criteria });

      return response.data;
    },

    async getMembers(_, { id }) {
      const res = await axios.get(`${API_URL}/childrenGroups/${id}/members`);

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async addToGroup(_, { id, itemsIds, type }) {
      const endpoint = type === "child" ? "children" : "classrooms";
      const payload = type === "child" ? { childrenIds: itemsIds } : { classroomsIds: itemsIds };

      const res = await axios.put(`${API_URL}/childrenGroups/${id}/${endpoint}`, payload);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },
  },
};

export default store;
